// Single BLE Heart Rate Monitor session.
//
// Sibling of ftmsDeviceSession — same imperative-factory pattern, same
// reconnect discipline, same ref-painting subscription model. Only the
// protocol differs: connects to 0x180D and subscribes to 0x2A37 notifications.
//
// HR is intentionally NOT modelled as another FTMS device: it's a global
// per-human signal that persists across equipment switches and floor-work
// gaps. The ConnectedEquipmentContext holds at most one HR session and
// surfaces it as `hr` alongside the FTMS device array.

import {
  HEART_RATE_MEASUREMENT_UUID,
  HEART_RATE_SERVICE_UUID,
} from "../utils/ble/constants";
import { parseHeartRateMeasurement } from "../utils/ble/heartRate";
import { waitForAdvertisement } from "../utils/ble/reconnect";
import { headlineDue, pageHidden } from "../utils/ble/headline";

const RECONNECT_DELAYS_MS = [1000, 3000, 9000];

export const STATUS = Object.freeze({
  IDLE: "idle",
  REQUESTING: "requesting",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  RECONNECTING: "reconnecting",
  ERROR: "error",
});

const EMPTY = Object.freeze({
  bpm: null,
  sensor_contact: "not_supported",
  energy_kj: null,
  rr_intervals_s: [],
});

const STORAGE_KEY = "ble.hrm.lastDevice";

/**
 * Remember (payload) or forget (null) the HR strap in this browser. Exported
 * for the same roster-seeding reason as writePersistedDevice.
 */
export const writePersistedHrm = (payload) => {
  try {
    if (payload) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* storage unavailable */
  }
};

export const readPersistedHrm = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Create one HRM session.
 *
 * @param {Object} opts
 * @param {string} opts.id
 * @param {string} opts.name
 * @param {() => void} opts.onChange - re-render trigger for the provider
 * @param {() => boolean} [opts.isActive] - whether a workout is running
 */
export function createBleHrSession({ id, name, isActive, onChange }) {
  let status = STATUS.IDLE;
  let error = null;
  let hint = null;
  let btDevice = null;
  let characteristic = null;
  let reconnectAttempt = 0;
  let reconnectTimer = null;
  let retrying = false;
  let lastHeadlineAt = 0;
  let lastPacketAt = 0;

  const metricsRef = { current: EMPTY };
  const subscribers = new Set();
  let snapshot = EMPTY;

  const setStatus = (next, errMsg = null, hintNext = null) => {
    status = next;
    error = errMsg;
    hint = hintNext;
    onChange?.();
  };

  const notify = (m) => {
    for (const cb of subscribers) {
      try {
        cb(m);
      } catch (err) {
        console.warn("[ble:hr] subscriber threw", err);
      }
    }
  };

  const handlePacket = (event) => {
    const view = event.target?.value;
    if (!view) return;
    let parsed;
    try {
      parsed = parseHeartRateMeasurement(view);
    } catch (err) {
      console.warn("[ble:hr] parse failed", err);
      return;
    }
    metricsRef.current = parsed;
    lastPacketAt = performance.now();

    notify(parsed);

    snapshot = parsed;
    const now = Date.now();
    if (headlineDue(lastHeadlineAt, now, { workoutActive: !!isActive?.(), hidden: pageHidden() })) {
      lastHeadlineAt = now;
      onChange?.();
    }
  };

  const getFreshSnapshot = (maxAgeMs = 3000) => {
    if (!lastPacketAt) return null;
    if (performance.now() - lastPacketAt > maxAgeMs) return null;
    return metricsRef.current;
  };

  const teardownNotifications = async () => {
    const char = characteristic;
    characteristic = null;
    if (!char) return;
    try {
      char.removeEventListener("characteristicvaluechanged", handlePacket);
    } catch {
      /* ignore */
    }
    try {
      await char.stopNotifications();
    } catch {
      /* ignore */
    }
  };

  // `scanMs`: listen for the device first, for gear handed back by
  // getDevices() that Chrome hasn't seen since it restarted (#169).
  const attach = async (device, { quiet = false, scanMs = 0 } = {}) => {
    if (!quiet) setStatus(STATUS.CONNECTING);
    if (scanMs && !(await waitForAdvertisement(device, scanMs))) {
      throw new Error("Couldn't find it. Wake it, then tap Reconnect.");
    }
    const server = await device.gatt.connect();
    const svc = await server.getPrimaryService(HEART_RATE_SERVICE_UUID);
    const char = await svc.getCharacteristic(HEART_RATE_MEASUREMENT_UUID);
    await char.startNotifications();
    char.addEventListener("characteristicvaluechanged", handlePacket);

    characteristic = char;
    btDevice = device;
    reconnectAttempt = 0;
    writePersistedHrm({ id, name });
    setStatus(STATUS.CONNECTED);
  };

  const scheduleReconnect = () => {
    if (reconnectTimer) return;
    const attempt = reconnectAttempt;
    if (attempt >= RECONNECT_DELAYS_MS.length) {
      // Out of retries — strap is probably off-body. Surface a hint so the
      // user knows to wake it rather than wait.
      setStatus(STATUS.RECONNECTING, "Strap hasn't reappeared.", "wake_hrm");
      return;
    }
    const delay = RECONNECT_DELAYS_MS[attempt];
    reconnectAttempt = attempt + 1;
    setStatus(STATUS.RECONNECTING, null, "wake_hrm");
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      if (!btDevice) return;
      try {
        await attach(btDevice);
      } catch {
        scheduleReconnect();
      }
    }, delay);
  };

  const handleDisconnect = () => {
    characteristic = null;
    if (status === STATUS.IDLE) return;
    scheduleReconnect();
  };

  const connect = async (device, { scanMs = 0 } = {}) => {
    if (status === STATUS.CONNECTING || status === STATUS.REQUESTING) return;
    setStatus(STATUS.CONNECTING);
    try {
      device.addEventListener("gattserverdisconnected", handleDisconnect);
      await attach(device, { scanMs });
    } catch (err) {
      console.warn("[ble:hr] connect failed", err);
      setStatus(STATUS.ERROR, err?.message || "Could not connect.");
    }
  };

  // Background retry while a workout runs. Quiet: it doesn't flash
  // "Connecting…" every pass, and a miss puts the visible state back exactly
  // as it was. Skips while the post-drop backoff is still running.
  const retry = async (device, { scanMs = 0 } = {}) => {
    if (retrying || reconnectTimer) return false;
    if (status === STATUS.CONNECTED || status === STATUS.CONNECTING || status === STATUS.REQUESTING) {
      return false;
    }
    retrying = true;
    const prev = { status, error, hint };
    try {
      device.addEventListener("gattserverdisconnected", handleDisconnect);
      await attach(device, { quiet: true, scanMs });
      return true;
    } catch {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      setStatus(prev.status, prev.error, prev.hint);
      return false;
    } finally {
      retrying = false;
    }
  };

  const disconnect = async () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    await teardownNotifications();
    if (btDevice) {
      try {
        btDevice.removeEventListener("gattserverdisconnected", handleDisconnect);
      } catch {
        /* ignore */
      }
      try {
        btDevice.gatt?.disconnect();
      } catch {
        /* ignore */
      }
    }
    btDevice = null;
    metricsRef.current = EMPTY;
    snapshot = EMPTY;
    lastPacketAt = 0;
    setStatus(STATUS.IDLE);
  };

  const forget = async () => {
    await disconnect();
    writePersistedHrm(null);
  };

  const subscribe = (cb) => {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
  };

  return {
    id,
    name,
    type: "hr_monitor",
    get status() {
      return status;
    },
    get error() {
      return error;
    },
    get hint() {
      return hint;
    },
    get snapshot() {
      return snapshot;
    },
    metricsRef,
    subscribe,
    connect,
    retry,
    disconnect,
    forget,
    getFreshSnapshot,
  };
}
